import { getBooleanFeatureGate } from "../featureGates";
var appSettings;
var disabledChangeGates;
var isFeatureEnabledCallback;
/**
 * Sets the app settings dictionary that contains a string->string mapping used by the application
 *  to check various settings, BRS, or flights.  If the settings contains a value named `DisabledChangeGates`
 *  will assume that is an object with the keys being the known disabled change gates.
 *  @param  settings - Dictionary containing various application settings
 */
export function setAppSettings(settings) {
    appSettings = settings;
    try {
        disabledChangeGates = JSON.parse(getAppSettingValue("DisabledChangeGates", "{}"));
    }
    catch (_a) {
        disabledChangeGates = {};
    }
}
/**
 * A test version of setAppSettings that returns a callback for reverting appSettings to their previous value
 * @param settings - Dictionary containing various application settings
 */
export function setTestAppSettings(settings) {
    var prevSettings = appSettings;
    setAppSettings(settings);
    return function () {
        setAppSettings(prevSettings);
    };
}
/**
 * Sets a callback to be used when a feature is checked to be enabled or not
 * @param  isFeatureEnabled - callback to be used to check when a feature is enabled
 */
export function setIsFeatureEnabledCallback(isFeatureEnabled) {
    isFeatureEnabledCallback = isFeatureEnabled;
}
/**
 * Check if a given feature is enabled. If an isFeatureEnabledCallback has been set it will use the value returned by
 * the callback. Otherwise, it will return true if the appSettings dictionary is defined and has an value equal to 'true' in the dictionary for the feature name.
 * Besides checking for appSettings dictionary value, it also checks for the boolean feature gate with the same name.
 * @param  featureName - the feature name
 * @param  defaultValue - optional default value when boolean feature gate not set (defaults to false when not specified)
 * @returns boolean - true if enabled
 */
export function isFeatureEnabled(feature, defaultValue) {
    if (defaultValue === void 0) { defaultValue = false; }
    var isSettingEnabled = isFeatureEnabledCallback
        ? isFeatureEnabledCallback(feature)
        : // Bug: 2691695 PowerPoint returns "True" instead of "true"
            !!appSettings &&
                !!appSettings[feature] &&
                appSettings[feature].toLowerCase() === "true";
    return isSettingEnabled || getBooleanFeatureGate(feature, defaultValue);
}
/** Get the value of a given appSetting. If the appSettings dictionary is defined and has a value for the feature name, it returns the value.
 * Otherwise it returns a defaultValue.
 * @param feature - the feature name
 * @param defaultValue - the default value to be returned.
 * @returns string - the value of the appSetting, if it exists.
 */
export function getAppSettingValue(feature, defaultValue) {
    if (!appSettings || !appSettings[feature]) {
        return defaultValue;
    }
    return appSettings[feature];
}
/**
 * Get the array value of a given AppSetting assuming that the app settings value is json array string
 * @param name  - app settings name
 * @returns array - the value of the app setting, if it exists. Otherwise, it returns an empty array
 */
export function getListAppSetting(name) {
    var value = getAppSettingValue(name, "[]" /* defaultValue */);
    return JSON.parse(value);
}
/**
 * This function can be used as a lightweight ChangeGate lookup. A single BRS setting can be used to indicate which ChangeGates have been disabled as a CSV.
 * To use:
 * 1. add a BRS setting in any of the apps for your package/feature, e.g. CatchUpOfficeOnlineDisabledChangeGates
 * 2. call this function in your gated code, e.g. isChangeGateEnabledInAppSetting('CatchUpOfficeOnlineDisabledChangeGates', 'FooChangeGate');
 * 3. to e-brake, override the BRS setting to include the change gate in the CSV
 * Note also that if the change gate is part of the appSetting's `DisabledChangeGates` setting, it'll also be considered here.
 * A change gate is enabled if it is NOT e-braked.  So a change gate is enabled if it is enabled BOTH in the local setting and
 * the gloabl one.
 * @param appSetting - the BRS setting containing the CSV of disabled ChangeGates
 * @param changeGate - change gate string to look for in CSV value of the BRS setting.
 * @returns bool - true if the ChangeGate is enabled, false if the ChangeGate has been e-braked.
 */
export function isChangeGateEnabledInAppSetting(appSetting, changeGate) {
    var changeGateCSV = getAppSettingValue(appSetting, "" /*defaultValue*/);
    return (!(disabledChangeGates && changeGate in disabledChangeGates) &&
        !changeGateCSV.split(",").some(function (c) { return c === changeGate; }));
}
//# sourceMappingURL=appSettings.js.map